import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useCRMHost } from '../CRMHostContext';
import { fileToDataUrl, toAuditEntries, toBannerPatch, toUiBanner } from '../adapters';
import type { BannerPatch, Tier as CrmTier } from '../crmClient';
import type { AuditEntry } from '../../components';
import { ScreenSkeleton } from '../../components/ScreenSkeleton';
import { BannerScreen, type BannerTierOption } from '../../screens/BannerScreen';
import { bannerStatus, type Banner } from '../../screens/loyaltyData';

/**
 * ต่อหน้าแบนเนอร์หน้าแรกเข้ากับ CRM module (FR-HOME-06)
 *
 * หน้าจอบันทึกทั้งรายการด้วยปุ่มเดียว แต่ backend แยกเป็นหลาย endpoint
 * ที่นี่จึงแตกออกเป็นขั้นตามลำดับ:
 *
 *   1. สร้างใบใหม่ (backend วางไว้ท้ายสุดเสมอ)
 *   2. แก้ใบเดิม — ส่งเฉพาะช่องที่เปลี่ยน
 *   3. ลบใบที่เอาออก — ใบที่ยังแสดงอยู่ต้องตั้งเป็นฉบับร่างก่อน ไม่งั้น backend ปฏิเสธ
 *   4. จัดลำดับ — ส่ง id ครบทุกใบในคำขอเดียว เฉพาะเมื่อลำดับต่างจากที่ backend จะมีอยู่แล้ว
 *
 * ⚠️ **ไม่มีคำขอเดียวที่บันทึกทั้งชุด** — ถ้าล้มกลางทาง ขั้นที่ทำไปแล้วจะไม่ถูกย้อน
 *    จึงโหลดข้อมูลจริงใหม่ทันทีและบอกผู้ใช้ตรง ๆ ว่าบันทึกได้บางส่วน
 *    ดีกว่าปล่อยให้หน้าจอแสดงค่าที่ไม่ตรงกับที่ลูกค้าเห็น
 *
 * ⚠️ การเขียนต้องเป็นสมาชิกของ division — ไม่เป็นจะอ่านได้แต่บันทึกไม่ได้ (403)
 */
export function BannerContainer() {
  const { api, dataMode } = useCRMHost();
  const isMock = dataMode === 'mock';

  const [banners, setBanners] = useState<Banner[] | undefined>(undefined);
  const [tiers, setTiers] = useState<CrmTier[]>([]);
  const [memberCounts, setMemberCounts] = useState<Map<string, number>>(new Map());
  /** ประวัติการแก้ไข — ของเสริม ล้มแล้วหน้ายังใช้งานได้ */
  const [auditEntries, setAuditEntries] = useState<AuditEntry[] | undefined>(undefined);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  /** โหลดรายการแบนเนอร์และประวัติใหม่ — ใช้หลังบันทึก ทั้งตอนสำเร็จและตอนล้มกลางทาง */
  const reload = useCallback(async (tierList: CrmTier[] = tiers) => {
    const [page, audit] = await Promise.all([
      api.listBanners(),
      api.listAuditLogs('banner').catch(() => null),
    ]);
    setBanners(page.items.map(toUiBanner));
    const lookup = { tierName: (id: number) => tierList.find((t) => t.id === id)?.name };
    setAuditEntries(audit ? toAuditEntries(audit.items, lookup) : undefined);
  }, [api, tiers]);

  const load = useCallback(() => {
    if (isMock) { setState('ready'); return; }
    let cancelled = false;
    setState('loading');

    Promise.all([
      api.listTiers(),
      /* จำนวนสมาชิกต่อระดับเป็นของเสริมของช่องกลุ่มผู้รับ ล้มแล้วหน้ายังใช้ได้ */
      api.listTierSummary().catch(() => null),
    ])
      .then(async ([tierPage, summary]) => {
        if (cancelled) return;
        setTiers(tierPage.items);
        setMemberCounts(
          summary ? new Map(summary.map((s) => [String(s.tier.id), s.totalAccounts])) : new Map(),
        );
        await reload(tierPage.items);
        if (!cancelled) setState('ready');
      })
      .catch(() => { if (!cancelled) setState('error'); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMock, api]);

  useEffect(() => load(), [load]);

  const tierOptions: BannerTierOption[] = useMemo(
    () => tiers.map((t) => ({ id: String(t.id), name: t.name, memberCount: memberCounts.get(String(t.id)) })),
    [tiers, memberCounts],
  );

  /**
   * เหตุผลจากกล่องยืนยันไปกับ**ทุก**ขั้น (สร้าง · แก้ · หยุดแสดงก่อนลบ · ลบ · จัดลำดับ)
   * การบันทึกครั้งเดียวกลายเป็นหลายแถวใน Audit Log ทุกแถวต้องตอบได้ว่าแก้เพราะอะไร
   */
  const onSave = useCallback(async (next: Banner[], prev: Banner[], changeReason?: string) => {
    const prevById = new Map(prev.map((b) => [b.id, b]));
    const nextIds = new Set(next.map((b) => b.id));
    /** id ของใบใหม่ที่หน้าจอสร้าง (`bn-<timestamp>`) → id จริงจาก backend */
    const createdIds = new Map<string, number>();
    let done = 0;

    /** ภาพที่ต้องส่ง — undefined = ไม่ต้องแตะ */
    const imageOf = async (b: Banner, before?: Banner): Promise<BannerPatch['image']> => {
      if (b.imageFile) return fileToDataUrl(b.imageFile);
      if (before?.imageUrl && !b.imageUrl) return null;
      return undefined;
    };

    const step = async (fn: () => Promise<unknown>) => { await fn(); done += 1; };

    try {
      for (const b of next) {
        const before = prevById.get(b.id);
        const patch = toBannerPatch(b, before);
        const image = await imageOf(b, before);
        if (image !== undefined) patch.image = image;

        if (!before) {
          await step(async () => { createdIds.set(b.id, (await api.createBanner({ ...patch, changeReason })).id); });
        } else if (Object.keys(patch).length > 0) {
          /* เติมเหตุผลหลังเช็กว่ามีค่าเปลี่ยน — ไม่งั้นทุกใบกลายเป็นมีการแก้ */
          await step(() => api.updateBanner(Number(b.id), { ...patch, changeReason }));
        }
      }

      for (const p of prev) {
        if (nextIds.has(p.id)) continue;
        /* backend ไม่ให้ลบใบที่ยังแสดงอยู่ — ผู้ใช้ตั้งเป็นฉบับร่างแล้วลบในรอบเดียวกัน
           แต่การตั้งเป็นฉบับร่างหายไปพร้อมใบที่ลบ จึงต้องหยุดแสดงให้ก่อนตรงนี้ */
        if (bannerStatus(p) === 'live') await step(() => api.updateBanner(Number(p.id), { isPublished: false, changeReason }));
        await step(() => api.deleteBanner(Number(p.id), changeReason));
      }

      /* ลำดับที่ backend มีอยู่ตอนนี้ = ใบเดิมตามลำดับเดิม แล้วต่อท้ายด้วยใบใหม่ตามลำดับที่สร้าง */
      const serverOrder = [
        ...[...prev].sort((a, b) => a.order - b.order).filter((b) => nextIds.has(b.id)).map((b) => Number(b.id)),
        ...next.filter((b) => createdIds.has(b.id)).map((b) => createdIds.get(b.id)!),
      ];
      const wanted = [...next].sort((a, b) => a.order - b.order).map((b) => createdIds.get(b.id) ?? Number(b.id));
      if (wanted.join() !== serverOrder.join()) await step(() => api.reorderBanners(wanted, changeReason));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'บันทึกไม่สำเร็จ';
      /* บางขั้นสำเร็จไปแล้ว — โหลดของจริงมาแสดง ไม่ให้หน้าจอค้างค่าที่ไม่ตรงกับที่ลูกค้าเห็น */
      if (done > 0) {
        await reload().catch(() => undefined);
        throw new Error(`บันทึกได้บางส่วน — ${msg} · หน้านี้โหลดข้อมูลล่าสุดมาแสดงแล้ว ตรวจแล้วบันทึกส่วนที่เหลืออีกครั้ง`);
      }
      throw err instanceof Error ? err : new Error(msg);
    }

    await reload();
  }, [api, reload]);

  if (isMock) return <BannerScreen />;
  if (state === 'loading' && banners === undefined) return <ScreenSkeleton title="แบนเนอร์หน้าแรก" blocks={4} />;

  return (
    <BannerScreen
      initialState={state === 'error' ? 'error' : 'ready'}
      banners={banners ?? []}
      tiers={tierOptions}
      onSave={onSave}
      auditEntries={auditEntries ?? []}
    />
  );
}
